import React, { useMemo, useState } from "react"

import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TooltipElement } from "@front-shared/elements/tooltip.element.tsx"

import { COVERAGE_CONFIG } from "../../config/coverage.config"
import { MultiProgressElement } from "../../elements/multiProgress.element"
import { FeatureFlagsHelper } from "../../helpers/featureFlags.helper"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { userDetailsSelector } from "../../store/selectors/userDetails.selector"
import type { IMultiProgressItem } from "../../types/objects.types.ts"

import chevronDownIcon from "@front-shared/assets/images/icons/chevron-down-dark.svg"
import chevronUpIcon from "@front-shared/assets/images/icons/chevron-up-dark.svg"

export const OrganizationQuoteComponent: React.FunctionComponent<
    IOrganizationQuoteComponentProps
> = props => {
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)

    const runtime = useAppSelector(runtimeSelector)

    const [isCollapsed, setIsCollapsed] = useState<boolean>(!!props.collapsible)

    // Generate the list of quota usage types that are enabled based on the feature flags
    const enabledQuotaUsageType = useMemo<QuotaUsageType[]>(() => {
        return Object.values(QuotaUsageType).filter(type => {
            // Skip ignored types
            if (COVERAGE_CONFIG.usageQuota.ignoredTypes.includes(type)) {
                return false
            }

            const config = COVERAGE_CONFIG.usageQuota.usageTypeConfig[type]

            // Check if the type has feature flags and if at least one is enabled
            if (config.featureFlags?.length) {
                return FeatureFlagsHelper.isFeatureFlagEnabled(
                    runtime.enabledFeatureFlags || [],
                    config.featureFlags,
                    config.featureFlagExistenceOperator,
                )
            }

            // If no feature flags defined, include the type
            return true
        })
    }, [runtime.enabledFeatureFlags])

    // Calculate the total number of checks already used
    const checksAlreadyUsed = useMemo<number>(() => {
        if (!selectedPracticeUsageQuota) {
            return 0
        }

        return enabledQuotaUsageType.reduce((memo, key) => {
            const amount = !selectedPracticeUsageQuota?.creditsUsedBreakdown?.[
                key
            ]
                ? 0
                : selectedPracticeUsageQuota.creditsUsedBreakdown[key]

            return memo + amount
        }, 0)
    }, [selectedPracticeUsageQuota, enabledQuotaUsageType])

    // Check if the soft quota limit has been exceeded
    const isSoftQuotaLimit = useMemo<boolean>(() => {
        return (
            !!selectedPracticeUsageQuota &&
            checksAlreadyUsed + (props.inProgressQuotaUsage || 0) >=
                selectedPracticeUsageQuota.softQuotaLimit
        )
    }, [
        selectedPracticeUsageQuota,
        checksAlreadyUsed,
        props.inProgressQuotaUsage,
    ])

    // Check if the hard quota limit has been exceeded
    const isHardQuotaLimit = useMemo<boolean>(() => {
        return (
            !!selectedPracticeUsageQuota &&
            checksAlreadyUsed + (props.inProgressQuotaUsage || 0) >=
                selectedPracticeUsageQuota.hardQuotaLimit
        )
    }, [
        selectedPracticeUsageQuota,
        checksAlreadyUsed,
        props.inProgressQuotaUsage,
    ])

    // Calculate the percentage of usage for a given amount
    const getUsagePercentage = (amount: number): number => {
        return Math.floor(
            (amount / (selectedPracticeUsageQuota?.softQuotaLimit || 1)) * 100,
        )
    }

    // Calculate the percentage of usage for each quota usage type
    const usageTypePercentages = useMemo<IMultiProgressItem[]>(() => {
        const quotaTypesBreakdown: IMultiProgressItem[] = []
        // Calculate the percentage of usage for each enabled quota usage type
        enabledQuotaUsageType.reduce<IMultiProgressItem[]>((memo, type) => {
            const config = COVERAGE_CONFIG.usageQuota.usageTypeConfig[type]

            const amount = !selectedPracticeUsageQuota?.creditsUsedBreakdown?.[
                type
            ]
                ? 0
                : selectedPracticeUsageQuota.creditsUsedBreakdown[type]

            return [
                ...memo,
                {
                    color: config.color,
                    percentage: getUsagePercentage(amount),
                },
            ]
        }, [])

        if (props.inProgressQuotaUsage) {
            quotaTypesBreakdown.push({
                color: COVERAGE_CONFIG.usageQuota.inProgressQuotaColor,
                percentage: getUsagePercentage(props.inProgressQuotaUsage),
            })
        }

        return quotaTypesBreakdown
    }, [
        selectedPracticeUsageQuota,
        props.inProgressQuotaUsage,
        enabledQuotaUsageType,
    ])

    return selectedPracticeUsageQuota ? (
        <div
            className={`
                relative px-[16px] rounded-[8px]
                ${
                    isHardQuotaLimit
                        ? "bg-brand-errorBg border border-solid border-brand-error"
                        : "bg-brand-warmLight"
                }
                ${!props.collapsible || !isCollapsed ? "pb-[16px]" : ""}
                ${props.className}
            `}
        >
            <div
                className={`
                    pt-[16px]
                    ${
                        props.collapsible
                            ? "cursor-pointer flex items-center justify-between pt-[16px]"
                            : ""
                    }
                    ${isCollapsed ? "pb-[16px]" : ""}
                `}
                onClick={
                    props.collapsible
                        ? () => {
                              setIsCollapsed(current => !current)
                          }
                        : undefined
                }
            >
                <PrimaryText
                    typography="subtitle"
                    color={
                        isHardQuotaLimit
                            ? NIRVANA_COLORS.brand.error
                            : NIRVANA_COLORS.brand.primary
                    }
                >
                    OneVerify Monthly Credits:{" "}
                    <span className="text-semibold">
                        {new Intl.NumberFormat().format(checksAlreadyUsed)} /{" "}
                        {new Intl.NumberFormat().format(
                            selectedPracticeUsageQuota.softQuotaLimit,
                        )}
                    </span>{" "}
                    Used
                </PrimaryText>

                {props.collapsible ? (
                    <img
                        alt="collapse"
                        src={isCollapsed ? chevronDownIcon : chevronUpIcon}
                    />
                ) : null}
            </div>

            {!isCollapsed || !props.collapsible ? (
                <>
                    <PrimaryText className="mt-[12px]" typography="textSmall">
                        Quota refers to checks made in OneVerify only - API
                        checks are not included
                    </PrimaryText>

                    <MultiProgressElement
                        className="mt-[16px]"
                        items={usageTypePercentages}
                    />

                    {props.inProgressQuotaUsage ? (
                        <div className="mt-[16px] flex">
                            <div className="flex items-center py-[8px] px-[16px] gap-[8px] bg-brand-whiteTransparent50 rounded-[4px]">
                                <div
                                    className="w-[12px] h-[12px] rounded-[3px] relative top-[-1px]"
                                    style={{
                                        backgroundColor:
                                            COVERAGE_CONFIG.usageQuota
                                                .inProgressQuotaColor,
                                    }}
                                />

                                <PrimaryText>This check will use</PrimaryText>

                                <PrimaryText typography="textSemibold">
                                    {props.inProgressQuotaUsage} credits
                                </PrimaryText>

                                {isSoftQuotaLimit || isHardQuotaLimit ? (
                                    <PrimaryText>
                                        {!isHardQuotaLimit
                                            ? "– you will exceed the monthly limit"
                                            : "but you've already exceeded your monthly limit"}
                                    </PrimaryText>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    <PrimaryText
                        className="mt-[16px]"
                        typography="caption"
                        weight="bold"
                    >
                        Credits Used Breakdown
                    </PrimaryText>

                    <div
                        className="
                            mt-[12px] flex items-center gap-4
                            md:block
                        "
                    >
                        {enabledQuotaUsageType.map((type, index) => {
                            const config =
                                COVERAGE_CONFIG.usageQuota.usageTypeConfig[type]

                            const creditsPerCheck =
                                selectedPracticeUsageQuota
                                    .checkTypeCreditsAmount[type]

                            return (
                                <div
                                    key={index}
                                    className="
                                        flex-1 flex gap-2
                                        md:mt-[8px]
                                    "
                                >
                                    <div
                                        className="w-[12px] h-[12px] rounded-[3px] relative top-[5px]"
                                        style={{
                                            backgroundColor: config.color,
                                        }}
                                    />

                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <PrimaryText typography="textSemibold">
                                                {config.label}
                                            </PrimaryText>

                                            {config.tooltip ? (
                                                <TooltipElement
                                                    className="ml-[4px]"
                                                    text={config.tooltip}
                                                />
                                            ) : null}

                                            <PrimaryText className="ml-[8px]">
                                                {new Intl.NumberFormat().format(
                                                    selectedPracticeUsageQuota
                                                        .creditsUsedBreakdown[
                                                        type
                                                    ],
                                                )}
                                            </PrimaryText>
                                        </div>

                                        <PrimaryText
                                            className="mt-[4px]"
                                            typography="caption"
                                        >
                                            {creditsPerCheck} credit
                                            {creditsPerCheck > 1 ? "s" : ""} per
                                            check
                                        </PrimaryText>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </>
            ) : null}
        </div>
    ) : null
}

interface IOrganizationQuoteComponentProps {
    className?: string
    collapsible?: boolean
    inProgressQuotaUsage?: number
}
